import AppUser from '@/entities/AppUser';
import { pushToDataLayer } from '@/utils/gtm';
import { onAuthStateChanged } from 'firebase/auth';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth } from '../firebase/FirebaseProvider';
import { getAppUser } from '../firebase/RemoteUser';

interface AuthContextProps {
  appUser: AppUser | null;
  loading: boolean;
  setAppUser: (appUser: AppUser) => void;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [appUser, setAppUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const appUser = await getAppUser();
          setAppUser(appUser);
          pushToDataLayer({
            user_firebase_id: appUser?.firestoreId || null,
          });
        } catch (error) {
          console.error('Failed to fetch app user:', error);
          setAppUser(null);
        }
      } else {
        setAppUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  return (
    <AuthContext.Provider value={{ appUser, loading, setAppUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
